import React from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';
import { FolderOpen } from 'lucide-react';
import { appRoutes } from 'src/routes';
import { useCompanyId } from 'src/features/companies/use-company-id';
import { Link } from 'src/components/ui/link';
import { CollapsibleCard, CollapsibleCardContent } from 'src/components/ui/collapsible-card';
import { useToggle } from 'src/lib/state-utils';

type Props = {
  title: string;
  parentId: string;
};

/**
 * Project Title widget — clickable link to the parent project page.
 *
 * Used both on the outcome detail view (Queued / Needs Attention) and on
 * the outcome's Revisions page (every other status), so editors/admins
 * always have a one-click path back to the parent. The previous design
 * relied on a Breadcrumbs "back" or a plain-text Parent reference at the
 * bottom of the Media Source block, both of which Karina flagged on
 * issue 2431.
 *
 * Nav stays in-tab (no target="_blank") to avoid the /login spin-up bug
 * that #2425 closed on PR #178.
 */
const ProjectTitleCard: React.FC<Props> = ({ title, parentId }) => {
  const { t } = useTranslation();
  const companyId = useCompanyId();
  const [open, setOpen] = useToggle(true);

  return (
    <CollapsibleCard
      icon={<FolderOpen />}
      title={t('common:project_title', { defaultValue: 'Project title' })!}
      open={open}
      onOpenChange={setOpen}
      variant={'shadow'}
      className={'tw-shadow-violet-200'}
    >
      <CollapsibleCardContent>
        <Link
          to={generatePath(appRoutes.request, {
            requestId: parentId,
            companyId: companyId ?? '',
          })}
          underline={'hover'}
          variant={'sky'}
          className={'tw-break-words tw-text-base tw-font-medium'}
        >
          {title}
        </Link>
      </CollapsibleCardContent>
    </CollapsibleCard>
  );
};

export { ProjectTitleCard };
